import * as Effects from 'redux-saga/effects'
import AdobeIMS from '@services/auth/IMS'
import {
  AspectRatio,
  ContentClass,
  generateImages,
  generateSimilarImages,
  resetState,
  setGeneratedImages,
  setImageGenerationStatus,
  setPropertyState,
  selectGeneratedImage,
  setVisibleToast,
  setShowOnboardingDialog,
  setShowLimitedFreeTimeToast,
  addDownloadURL,
  setDownloadURLs,
  setShowFreeUserDialog,
  setShowPaywall,
  setOrderStatus,
  setAccessProfileWorkflowResult,
  setPaywallURL,
  setShowCreditWarningToast,
  FireflyState,
  setFireflyCreditPlan,
  loadFireflyCreditPlan,
  setShowPaywallToast,
  stopImageGeneration
} from '@store/slices/fireflySlice'
import { EngineCapturePayload } from '@services/engine/types'
import createEventChannel from '@store/middleware/createEventChannel'
import Context from '@store/middleware/context'
import { EventChannel } from 'redux-saga'
import { exportCapture, setCaptureStatus } from '@store/slices/sceneSlice'
import { dispatchFromStore, PayloadType, RootState } from '@store/store'
import { ITokenInformation } from '@identity/imslib/adobe-id/custom-types/CustomTypes'
import { setShowFireflyPopover } from '@store/slices/projectSlice'
import { PayloadAction } from '@reduxjs/toolkit'
import { setPropertyState as setScenePropertyState } from '@store/slices/sceneSlice'
import * as Sentry from '@sentry/nextjs'
import {
  LocalStorageBooleanValue,
  LocalStorageKey
} from 'constants/localStorage'
import { getUserLocalePreference } from '@concerns/i18n/browser'
import { LocaleString } from '@concerns/i18n/types'

// The data structure returned from the Access Platform API
type AccessProfileResponse = {
  asnp: {
    payload: string
    asnpSpecVersion: string
  }
  workflow?: {
    id: string
    instanceId: string
    response: string
    type: string
    version: string
  }
}

// The data structure of Access Profile
export type AccessProfile = {
  appProfile: AccessAppProfile
}

type AccessAppProfile = {
  accessibleItems?: AccessibleItem[]
}

/**
 * The data associated with an accessible item.
 */
type AccessibleItem = {
  status: AccessibleItemStatus
  fulfillable_items?: FulfillableItems
}

/**
 * The possible status values for an accessible item.
 */
export enum AccessibleItemStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

/**
 * A collection of info about fulfillable items that is keyed off of the fulfillable item code.
 */
type FulfillableItems = Record<string, FulfillableItem | undefined>

/**
 * The fulfillable item data that is associated with an accessible item.
 */
type FulfillableItem = {
  enabled: boolean

  charging_model?: {
    model?: string
    cap?: number
    unit?: string
    overage?: string
    rollover?: number
    levels?: number
  }
  feature_sets?: FeatureSets
  status_override_reason?: StatusOverrideReason
}

type FeatureSets = Record<FeatureKey, Feature | undefined>

type Feature = {
  id: string
  label: string
  enabled: boolean
}

enum StatusOverrideReason {
  LegalMisuseBlock = 'LEGAL_MISUSE_BLOCK'
}

/**
 * The entitlement payload
 */
export type Entitlement = {
  isFree: boolean
  isPaid: boolean
  isHardCapped: boolean
  isSoftCapped: boolean
  fireflyPlan: (typeof FireflyPlans)[number] | null
  isPaidPremium: boolean
}

export const ENTITLEMENT_BLOCK: Entitlement = {
  isPaid: false,
  isFree: false,
  isHardCapped: false,
  isSoftCapped: false,
  fireflyPlan: null,
  isPaidPremium: false
}

const FeatureKeys = [
  'basic',
  'premium',
  'hard_capped',
  'soft_capped',
  'hard_capped_limit_2',
  'k12',
  'unlimited',
  'standard_credits',
  'premium_credits',
  'ff_lvl_00_free',
  'ff_lvl_10_single_app',
  'ff_lvl_20_all_apps',
  'ff_lvl_30_pro',
  'ff_lvl_40_video'
] as const

type FeatureKey = (typeof FeatureKeys)[number]

// A fulfillable item is a feature that an Adobe user subscribes to. It can also be considered a subscription plan. This is frequently referred to as an FI. Different apps can have different FIs for the same user.
export type FireflyFulfillableItemCreditModel =
  | 'UNAVAILABLE'
  | 'FREE-EXHAUSTABLE'
  | 'PAID-UNEXHAUSTABLE'
  | 'PAID-EXHAUSTABLE'

const FireflyPlans = [
  'FF_FREE',
  'FF_SINGLE',
  'FF_ALL',
  'FF_5',
  'FF_10',
  'FF_30'
] as const

export type CreditBalanceDataResponse = {
  total: {
    quota: {
      total: number
      available: number
      used: number
    }
    planCap: string
    availableUntil: string // ISO format
  }
  credits: {
    firefly_free_credit: {
      quota: {
        available: number
        total: number
        used: number
      }
    }
  }
}

// Allowed options (2688, 1536), (896, 1152), (1344, 768), (2304, 1792), (1152, 896), (2048, 2048), (1792, 2304), (1024, 1024)
const AspectRatioSizeMap = {
  [AspectRatio.LANDSCAPE]: { width: 2304, height: 1792 },
  [AspectRatio.PORTRAIT]: { width: 1792, height: 2304 },
  [AspectRatio.SQUARE]: { width: 2048, height: 2048 },
  [AspectRatio.WIDESCREEN]: { width: 2688, height: 1536 }
} as const

// https://github.com/redux-saga/redux-saga/issues/2018
const { all, call, put, select, takeEvery, take, fork, race, delay }: any =
  Effects

let generateImgApiReqAbortController: AbortController
let generateSimilarImgApiReqAbortController: AbortController

function* handleGenerateImages() {
  while (true) {
    try {
      yield take(generateImages.type)

      const { prompt }: RootState['firefly'] = yield select(
        (state: RootState) => state.firefly
      )
      if (!prompt) {
        throw new Error('Firefly - Prompt required generating firefly img')
      }

      yield put(setImageGenerationStatus('generating-new-images'))

      // delay is required because the main UI thread gets blocked when the engine exports images which prevents the loading spinner to start it's animation until exporting is completed
      yield delay(1000)

      yield put(exportCapture({ format: 'reference' }))

      generateImgApiReqAbortController = new AbortController()

      const { cancel } = yield race({
        task: call(initializeCaptureChannel),
        cancel: take(stopImageGeneration.type)
      })

      if (cancel) {
        generateImgApiReqAbortController?.abort()
      }
    } catch (error) {
      yield put(setVisibleToast('error'))
      yield put(stopImageGeneration())
      console.error('handleGenerateImages error', error)
    }
  }
}

/**
 * When firefly view is displayed, frame needs to be enabled with the same size as the selected aspect ratio from the firefly panel
 * When firefly view is hidden, reset back to original frame property values
 */
function* changeFramePropertiesOnPopoverChange({
  payload
}: PayloadAction<PayloadType<typeof setShowFireflyPopover>>) {
  const { firefly, scene }: RootState = yield select(
    (state: RootState) => state
  )

  const { aspectRatio, previousSceneFramePropertiesOnShow } = firefly
  const { frameSize, frameType, frameEnabled } = scene

  if (payload) {
    // Store previous value of frame properties when showing firefly view to revert changes back to original values when firefly view is not displayed
    yield put(
      setPropertyState({
        key: 'previousSceneFramePropertiesOnShow',
        value: {
          frameEnabled,
          frameSize,
          frameType
        }
      })
    )

    const fireflySize = AspectRatioSizeMap[aspectRatio]

    if (frameEnabled) {
      yield put(
        setScenePropertyState({
          key: 'frameEnabled',
          value: false
        })
      )
    }

    // Update scene frame properties because the depth img generated from the engine should use the same size as the selected aspect ratio from the firefly panel
    if (frameType !== 0) {
      yield put(
        setScenePropertyState({
          key: 'frameType',
          value: 0
        })
      )
    }

    yield put(
      setScenePropertyState({
        key: 'frameSize',
        value: { h: fireflySize.height, w: fireflySize.width }
      })
    )

    return
  }

  // Revert frame properties back to original values before firefly view is shown
  if (previousSceneFramePropertiesOnShow) {
    if (previousSceneFramePropertiesOnShow.frameEnabled !== frameEnabled) {
      yield put(
        setScenePropertyState({
          key: 'frameEnabled',
          value: previousSceneFramePropertiesOnShow.frameEnabled
        })
      )
    }

    if (previousSceneFramePropertiesOnShow.frameType !== frameType) {
      yield put(
        setScenePropertyState({
          key: 'frameType',
          value: previousSceneFramePropertiesOnShow.frameType
        })
      )
    }

    yield put(
      setScenePropertyState({
        key: 'frameSize',
        value: previousSceneFramePropertiesOnShow.frameSize
      })
    )

    yield put(
      setPropertyState({
        key: 'previousSceneFramePropertiesOnShow',
        value: null
      })
    )
  }

  yield put(setVisibleToast('none'))
}

function* handleSetPropertyState({
  payload
}: PayloadAction<PayloadType<typeof setPropertyState>>) {
  if (payload.key === 'aspectRatio') {
    // Engine frame size should always be set to the same value as aspect ratio size
    const { height, width } =
      AspectRatioSizeMap[payload.value as FireflyState['aspectRatio']]
    yield put(
      setScenePropertyState({
        key: 'frameSize',
        value: { h: height, w: width }
      })
    )
  }
}

function* handleResetState() {
  const { styleImageReference }: RootState['firefly'] = yield select(
    (state: RootState) => state.firefly
  )

  if (styleImageReference?.type === 'user-upload') {
    yield URL.revokeObjectURL(styleImageReference.url)
  }

  yield clearDownloadURLs()
}

function* initializeCaptureChannel() {
  const dataChannel: EventChannel<EngineCapturePayload> = yield call(
    createEventChannel,
    Context.Engine?.EngineDataChannel,
    'capture_reference_image_data'
  )

  while (true) {
    try {
      yield put(
        setCaptureStatus({
          format: 'reference',
          status: 'completed'
        })
      )

      const {
        objectURL,
        depthObjectURL,
        colorObjectURL
      }: EngineCapturePayload = yield take(dataChannel)
      // saveAs(objectURL, 'object.png')
      // saveAs(depthObjectURL, 'depth.png')
      // saveAs(colorObjectURL, 'color.png')

      yield fork(
        handleReferenceCapture,
        objectURL,
        depthObjectURL,
        colorObjectURL
      )
    } catch (err) {
      console.error(err)
      Sentry.captureException(err)
      yield put(stopImageGeneration())
    }
  }
}

function* handleReferenceCapture(
  edgeBlobUrl: URL,
  depthBlobUrl: URL,
  colorBlobUrl: URL
) {
  const {
    aspectRatio,
    contentClass,
    prompt,
    stylePresets,
    styleImageReference,
    imgReference,
    structureStrength,
    styleStrength,
    guideStrength,
    visualIntensity
  }: RootState['firefly'] = yield select((state: RootState) => state.firefly)

  const accessToken: ITokenInformation | null = yield call(
    AdobeIMS.getAccessTokenAsync
  )
  if (!accessToken || !process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID) {
    throw new Error('Firefly - Required fields missing')
  }
  try {
    if (!edgeBlobUrl) throw new Error('Firefly - Img missing')
    const styleImgReferenceBlob: null | Blob = styleImageReference
      ? yield call(() => fetch(styleImageReference.url).then(res => res.blob()))
      : null

    const edgeBlob: Blob = yield call(() =>
      fetch(edgeBlobUrl).then(res => res.blob())
    )

    // we should always have a depth image regardless if we use it or not
    if (!depthBlobUrl) throw new Error('Firefly - Img missing')

    const depthBlob: Blob = yield call(() =>
      fetch(depthBlobUrl).then(res => res.blob())
    )

    const colorBlob: Blob = yield call(() =>
      fetch(colorBlobUrl).then(res => res.blob())
    )

    const imgGuidances: StoreImgGuidancesResponse = yield call(
      storeImgGuidances,
      [
        {
          type: 'depth',
          // edgeBlob can be used as a reference as well if 'depth' is not enabled
          blob: imgReference === 'depth' && depthBlobUrl ? depthBlob : edgeBlob
        },
        ...(styleImgReferenceBlob
          ? [{ type: 'style', blob: styleImgReferenceBlob }]
          : []),
        { type: 'canny', blob: edgeBlob },
        { type: 'color', blob: colorBlob }
      ],
      accessToken.token,
      process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID
    )

    const depthDataImgId = imgGuidances?.find(
      ({ type }) => type === 'depth'
    )?.imgId
    const styleDataImgId = imgGuidances?.find(
      ({ type }) => type === 'style'
    )?.imgId
    const cannyDataImgId = imgGuidances?.find(
      ({ type }) => type === 'canny'
    )?.imgId
    const colorDataImgId = imgGuidances?.find(
      ({ type }) => type === 'color'
    )?.imgId

    if (!depthDataImgId) {
      throw new Error('Firefly - Missing ImgId')
    }

    const locale = getUserLocalePreference()

    const body = {
      prompt,
      ...(contentClass !== ContentClass.AUTO && { contentClass }),
      size: AspectRatioSizeMap[aspectRatio],
      visualIntensity,
      locale,
      modelVersion: 'image3_fast',
      seeds: generateUniqueRandomNumbers(100, 45000, 4),
      controlData: {
        depthData: {
          // use estimator when packed depth image is not available
          mode: imgReference === 'depth' ? 'unpack_rgba' : 'estimator',
          adherenceThresholdOverride: structureStrength,
          referenceImage: {
            id: depthDataImgId
          }
        },
        // we can only use edge image for cannyData when depth is enabled
        ...(imgReference === 'depth' && {
          cannyData: {
            adherenceThresholdOverride: structureStrength,
            mode: 'gray_thresholding',
            referenceImage: {
              id: cannyDataImgId
            }
          }
        })
      },
      ...(guideStrength && {
        editData: {
          guideImage: {
            id: colorDataImgId
          },
          guideStrength
        }
      }),
      styles: {
        presets: stylePresets,
        strength: styleStrength > 0 ? styleStrength : 1,
        ...(styleDataImgId && { referenceImage: { id: styleDataImgId } })
      }
    }

    const generatedImgResponse: GeneratedImagesResponse = yield call(
      generateFireflyImages,
      JSON.stringify(body),
      accessToken.token
    )
    const { error_code: errorCode } = generatedImgResponse
    if (errorCode) {
      yield call(handleClioGenerateErrorCode, errorCode)
      return
    }

    if (generatedImgResponse.outputs.length) {
      const images = generatedImgResponse.outputs.map(({ image }, index) => ({
        url: image.presignedUrl,
        selected: index === 0
      }))

      yield put(setGeneratedImages(images))
    }

    if (generatedImgResponse.promptHasDeniedWords) {
      yield put(setVisibleToast('warning'))
    } else {
      yield put(setVisibleToast('completed'))
    }
  } catch (error) {
    yield put(setVisibleToast('error'))
    console.error('handleGenerateImages error', error)
  } finally {
    yield put(stopImageGeneration())
  }
}

function* handleClioGenerateErrorCode(errorCode: string) {
  if (errorCode === 'access_error') {
    // todo get custom headers
    yield put(setShowPaywall('quota'))
  } else if (errorCode?.includes('prompt')) {
    yield put(setVisibleToast('warning'))
  } else {
    yield put(setVisibleToast('error'))
  }
}

function* handleStopImageGeneration() {
  yield put(setImageGenerationStatus('idle'))
}

function* handleGenerateSimilarImages({
  payload
}: PayloadAction<PayloadType<typeof generateSimilarImages>>) {
  try {
    const accessToken: ITokenInformation | null = yield call(
      AdobeIMS.getAccessTokenAsync
    )
    if (!accessToken || !process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID) {
      throw new Error('Firefly - Required fields missing')
    }

    const { aspectRatio, generatedImages }: RootState['firefly'] = yield select(
      (state: RootState) => state.firefly
    )

    const selectedImage = generatedImages.find(({ selected }) => selected)
    if (!selectedImage) throw new Error('Firefly - No image selected')

    yield put(setImageGenerationStatus('generating-similar-images'))
    yield put(
      setGeneratedImages([
        selectedImage,
        { selected: false },
        { selected: false },
        { selected: false }
      ])
    )

    generateSimilarImgApiReqAbortController = new AbortController()

    const blob: Blob = yield call(() =>
      fetch(payload.imgUrl).then(res => res.blob())
    )

    const storedImage: StoreImgGuidancesResponse = yield call(
      storeImgGuidances,
      [
        {
          type: 'ref',
          blob
        }
      ],
      accessToken.token,
      process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID
    )

    const storedImgId = storedImage.length && storedImage[0].imgId

    if (!storedImgId) {
      throw new Error('Firefly - Missing ImgId')
    }

    const body = {
      image: {
        id: storedImgId
      },
      seeds: generateUniqueRandomNumbers(100, 45000, 3),
      size: AspectRatioSizeMap[aspectRatio]
    }

    const { task, cancel }: { task: GeneratedImagesResponse; cancel: any } =
      yield race({
        task: call(
          generateSimilarFireflyImages,
          JSON.stringify(body),
          accessToken.token
        ),
        cancel: take(stopImageGeneration.type)
      })

    const { error_code: errorCode } = task

    if (errorCode) {
      yield call(handleClioGenerateErrorCode, errorCode)
      return;
    }

    if (cancel) {
      generateSimilarImgApiReqAbortController?.abort()
    } else {
      const images = task.outputs.map(({ image }) => ({
        url: image.presignedUrl,
        selected: false
      }))

      yield put(setGeneratedImages([selectedImage, ...images]))
      yield put(setVisibleToast('completed'))
    }
  } catch (error) {
      console.error('handleGenerateSimilarImages error', error)
      yield put(setVisibleToast('error'))
  } finally {
    yield put(stopImageGeneration())
  }
}
function* handleSelectGeneratedImage({
  payload
}: PayloadAction<PayloadType<typeof selectGeneratedImage>>) {
  const { generatedImages }: RootState['firefly'] = yield select(
    (state: RootState) => state.firefly
  )

  yield put(
    setGeneratedImages(
      generatedImages.map(img => ({
        ...img,
        selected: img.url === payload.url
      }))
    )
  )
}

function* showOnboardingDialog({
  payload
}: PayloadAction<PayloadType<typeof setShowFireflyPopover>>) {
  const showOnboardingDialog =
    payload &&
    localStorage.getItem(LocalStorageKey.fireflyViewedOnboardingDialog) !==
      LocalStorageBooleanValue.TRUE

  if (showOnboardingDialog) {
    yield put(setShowOnboardingDialog(true))
  }
}

function* closeLimitedFreeTimeToastOnShow({
  payload
}: PayloadAction<PayloadType<typeof setShowLimitedFreeTimeToast>>) {
  if (payload) {
    yield delay(6000)
    yield put(setShowLimitedFreeTimeToast(false))
  }
}

function* handleAddDownloadURL({
  payload
}: PayloadAction<PayloadType<typeof addDownloadURL>>) {
  const { downloadURLs }: RootState['firefly'] = yield select(
    (state: RootState) => state.firefly
  )

  yield put(setDownloadURLs([...downloadURLs, payload]))
}

function* clearDownloadURLs() {
  const { downloadURLs }: RootState['firefly'] = yield select(
    (state: RootState) => state.firefly
  )

  downloadURLs.forEach(({ downloadURL }) => {
    URL.revokeObjectURL(downloadURL)
  })

  yield put(setDownloadURLs([]))
}

function* handleSetAccessProfileWorkflowResult({
  payload
}: PayloadAction<
  PayloadType<typeof setAccessProfileWorkflowResult>
>): Generator<any, void, any> {
  const accessToken = AdobeIMS.getAccessToken()?.token
  if (!accessToken) return

  const { locale, response } = payload!

  const workflowResult = {
    id: 'ondemand_purchase_subscription_workflow',
    response,
    type: 'WEB_APP_MODAL_WORKFLOW',
    instanceId: crypto.randomUUID(),
    version: '2'
  }

  const appDetails = getAccessProfileAppDetails(locale)

  const body = JSON.stringify({ appDetails, workflowResult })
  const { workflow } = yield fetchAccessProfileWorkflow({
    body,
    accessToken
  })

  yield put(setPaywallURL(workflow?.entryUrl))
}

function* handleSetFireflyCreditPlan({
  payload
}: PayloadAction<PayloadType<typeof setFireflyCreditPlan>>) {
  const { fulfillableItemCreditModel } = payload

  if (fulfillableItemCreditModel === 'FREE-EXHAUSTABLE') {
    yield put(
      setShowFreeUserDialog(
        !localStorage.getItem(
          LocalStorageKey.fireflyViewedFreeUserOnboardingDialog
        )
      )
    )
  }
}

function* handleSetOrderStatus({
  payload
}: PayloadAction<PayloadType<typeof setOrderStatus>>) {
  // Need to refetch the credit model so that the UI is updated
  if (payload === 'ORDER_COMPLETE') yield put(loadFireflyCreditPlan())
}

async function storeImgGuidance(
  blob: Blob,
  token: string,
  apiKey: string
): Promise<string | undefined> {
  return await fetch(
    `${process.env.NEXT_PUBLIC_CLIENT_FIREFLY_IMAGE_ENDPOINT}`,
    {
      headers: {
        'Access-Control-Allow-Origin': '*',
        accept: 'application/json',
        'accept-language': 'en-US,en;q=0.9,pt;q=0.8',
        authorization: `Bearer ${token}`,
        'content-type': 'image/png',
        'sec-fetch-dest': 'empty',
        'sec-fetch-mode': 'cors',
        'sec-fetch-site': 'cross-site',
        'x-api-key': apiKey
      },
      body: blob,
      method: 'POST',
      mode: 'cors'
    }
  )
    .then(res => res.json())
    .then(res => res?.images?.length && res.images[0]['id'])
}

type ImgGuidance = {
  blob: Blob
  type: string
}

type StoreImgGuidancesResponse = Array<{
  type: string
  imgId: string | undefined
}>

async function storeImgGuidances(
  guidances: ImgGuidance[],
  token: string,
  apiKey: string
): Promise<StoreImgGuidancesResponse> {
  return Promise.all(
    guidances.map(async ({ blob, type }) => {
      const imgId = await storeImgGuidance(blob, token, apiKey)
      return { type, imgId }
    })
  )
}

type GeneratedImagesResponse = {
  outputs: Array<{
    seed: number
    image: { id: string; presignedUrl: string }
  }>
  promptHasDeniedWords?: boolean
  size: { width: number; height: number }
  error_code?: string
  stack_trace?: Array<string>
  headers?: Record<string, string>
}

async function createGenerateImagesRequest(
  endpoint: RequestInfo | URL,
  body: RequestInit['body'],
  token: string,
  abortSignal: RequestInit['signal']
): Promise<GeneratedImagesResponse> {
  const response = await fetch(endpoint, {
    headers: {
      accept: 'application/json',
      'accept-language': 'en-US,en;q=0.9,pt;q=0.8',
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      'sec-fetch-dest': 'empty',
      'sec-fetch-mode': 'cors',
      'sec-fetch-site': 'cross-site',
      'x-api-key': process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID!
    },
    body,
    method: 'POST',
    mode: 'cors',
    signal: abortSignal
  })

  const data: GeneratedImagesResponse = await response.json()

  const { error_code } = data

  if (!error_code) {
    const creditBalanceData = await fetchCreditBalanceData(token)
    // totalCredits is the sum of used credits and unused credits
    const totalCredits = creditBalanceData.total.quota.total
    const availableCredits = creditBalanceData.total.quota.available

    // first check is necessary to make sure the quotient afterwards is well-defined
    const showCreditWarningToast =
      totalCredits > 0 && availableCredits / totalCredits <= 0.2
    dispatchFromStore(setShowCreditWarningToast(showCreditWarningToast))
  }

  return data
}

async function generateFireflyImages(
  body: string,
  token: string
): Promise<GeneratedImagesResponse> {
  const response = await createGenerateImagesRequest(
    process.env.NEXT_PUBLIC_CLIENT_FIREFLY_GENERATE_ENDPOINT,
    body,
    token,
    generateImgApiReqAbortController?.signal
  )

  return response
}

async function generateSimilarFireflyImages(
  body: string,
  token: string
): Promise<GeneratedImagesResponse> {
  const response = await createGenerateImagesRequest(
    process.env.NEXT_PUBLIC_CLIENT_FIREFLY_GENERATE_SIMILAR_ENDPOINT,
    body,
    token,
    generateSimilarImgApiReqAbortController?.signal
  )

  return response
}

async function fetchAccessProfileWorkflow({
  body,
  accessToken
}: {
  body: string
  accessToken: string
}): Promise<AccessProfileResponse> {
  return await fetch(process.env.NEXT_PUBLIC_CLIENT_ACCESS_PROFILE_ENDPOINT!, {
    method: 'POST',
    headers: {
      Authorization: accessToken,
      'X-Api-Key': process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID!,
      'Content-Type': 'application/json'
    },
    body
  }).then(res => res.json())
}

function generateUniqueRandomNumbers(
  min: number,
  max: number,
  total: number
): number[] {
  if (total > max - min + 1) {
    console.error('Total count exceeds range of possible unique numbers')
    return []
  }

  const uniqueNumbers: number[] = []
  while (uniqueNumbers.length < total) {
    const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min
    if (!uniqueNumbers.includes(randomNumber)) {
      uniqueNumbers.push(randomNumber)
    }
  }
  return uniqueNumbers
}

function getAccessProfileAppDetails(locale: LocaleString) {
  return {
    nglAppId: process.env.NEXT_PUBLIC_CLIENT_ACCESS_PROFILE_PROJECT_NEO_APP_ID,
    nglAppVersion: '1.0',
    nglLibRuntimeMode: 'NAMED_USER_ONLINE',
    locale
  }
}

async function fetchAccessProfile(
  accessToken: string
): Promise<AccessProfile | null> {
  const locale = getUserLocalePreference()
  const appDetails = getAccessProfileAppDetails(locale)

  try {
    const response = await fetch(
      process.env.NEXT_PUBLIC_CLIENT_ACCESS_PROFILE_ENDPOINT!,
      {
        method: 'POST',
        headers: {
          Authorization: accessToken,
          'X-Api-Key': process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID!,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appDetails
        })
      }
    )

    const json: AccessProfileResponse = await response.json()
    const buffer = Buffer.from(json?.asnp?.payload, 'base64')
    const deserializedAccessProfile = buffer.toString('utf-8')

    return JSON.parse(deserializedAccessProfile)
  } catch (err) {
    console.error(err)

    return null
  }
}

function parseEntitlement(accessProfile: AccessProfile): Entitlement {
  const userEntitlement: Entitlement = Object.create(ENTITLEMENT_BLOCK)
  const accessibleItems = accessProfile?.appProfile?.accessibleItems ?? []

  accessibleItems.forEach(accessibleItem => {
    if (
      accessibleItem.status !== AccessibleItemStatus.ACTIVE ||
      !accessibleItem.fulfillable_items
    )
      return

    const featureSets =
      accessibleItem.fulfillable_items['firefly_credits']?.feature_sets

    if (featureSets) {
      userEntitlement.isFree =
        userEntitlement.isFree || !!featureSets['basic']?.enabled
      userEntitlement.isPaid =
        userEntitlement.isPaid || !!featureSets['premium']?.enabled
      userEntitlement.isPaidPremium =
        userEntitlement.isPaidPremium ||
        !!featureSets['premium_credits']?.enabled
      userEntitlement.isHardCapped =
        userEntitlement.isHardCapped || !!featureSets['hard_capped']?.enabled
      // In the absence of both hard and soft cap markers treat user as soft cap.
      // More details in https://jira.corp.adobe.com/browse/DVAGENAI-3248
      userEntitlement.isSoftCapped =
        userEntitlement.isSoftCapped ||
        !!featureSets['soft_capped']?.enabled ||
        (featureSets['soft_capped']?.enabled === undefined &&
          featureSets['hard_capped']?.enabled === undefined)
      userEntitlement.fireflyPlan = getFireflyPlan(featureSets)
    }
  })

  if (userEntitlement.isPaid) userEntitlement.isFree = false
  // If any value of isSoftCapped is true from above iteration then isHardCapped should be false.
  if (userEntitlement.isSoftCapped) userEntitlement.isHardCapped = false

  return userEntitlement
}

function getFireflyFulfillableItemCreditModelFromEntitlement(
  entitlement: Entitlement
): FireflyFulfillableItemCreditModel {
  const { isPaid, isPaidPremium, isHardCapped, fireflyPlan, isSoftCapped } =
    entitlement

  if (!isPaid && isHardCapped) return 'FREE-EXHAUSTABLE'

  if (isPaid && isHardCapped && fireflyPlan === 'FF_5') {
    return 'PAID-EXHAUSTABLE'
  }

  if (
    isSoftCapped ||
    ((isPaid || isPaidPremium) && isHardCapped && fireflyPlan !== 'FF_5')
  ) {
    return 'PAID-UNEXHAUSTABLE'
  }

  return 'UNAVAILABLE'
}

export async function getUserFireflyFulfillableItemCreditModel(
  accessToken: string
): Promise<FireflyFulfillableItemCreditModel | null> {
  const accessProfile = await fetchAccessProfile(accessToken)
  if (!accessProfile) return 'UNAVAILABLE'
  const entitlement = parseEntitlement(accessProfile)
  const fiCreditModel =
    getFireflyFulfillableItemCreditModelFromEntitlement(entitlement)

  return fiCreditModel
}

function getFireflyPlan(featureSets: FeatureSets) {
  if (featureSets['ff_lvl_40_video']?.enabled) {
    return 'FF_30'
  } else if (featureSets['ff_lvl_30_pro']?.enabled) {
    return 'FF_10'
  } else if (
    featureSets['ff_lvl_10_single_app']?.enabled &&
    featureSets['hard_capped_limit_2']?.enabled
  ) {
    return 'FF_5'
  } else if (featureSets['ff_lvl_20_all_apps']?.enabled) {
    return 'FF_ALL'
  } else if (featureSets['ff_lvl_10_single_app']?.enabled) {
    return 'FF_SINGLE'
  } else if (featureSets['ff_lvl_00_free']?.enabled) {
    return 'FF_FREE'
  }
  console.warn('No Firefly Plan found return FF_FREE')
  return 'FF_FREE'
}

async function fetchCreditBalanceData(
  accessToken: string
): Promise<CreditBalanceDataResponse> {
  const response = await fetch(
    process.env.NEXT_PUBLIC_CREDIT_BALANCE_ENDPOINT,
    {
      headers: {
        Authorization: accessToken,
        'X-Api-Key': process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID!,
        'Content-Type': 'application/json'
      }
    }
  )

  const data = await response.json()
  return data
}

function* handleLoadFireflyCreditPlan() {
  const token = AdobeIMS.getAccessToken()?.token
  if (!token) return
  const accessProfile: AccessProfile = yield fetchAccessProfile(token)
  const entitlement = parseEntitlement(accessProfile)
  const fulfillableItemCreditModel =
    getFireflyFulfillableItemCreditModelFromEntitlement(entitlement)

  const isEnterpriseUser: boolean = yield AdobeIMS.isEnterpriseUser()
  const canUpgrade = getFireflyUpgradeEligibility(isEnterpriseUser, entitlement)

  yield put(
    setFireflyCreditPlan({
      fulfillableItemCreditModel,
      canUpgrade
    })
  )
}

// We use the same logic that spl-web-client uses to determine if a user can upgrade their plan.
// See body of function getToastPaymentButtonLabel in CommerceUtils.tsx in spl-web-client repo
function getFireflyUpgradeEligibility(
  isEnterpriseUser: boolean,
  entitlement: Entitlement
): boolean {
  const { fireflyPlan, isPaidPremium, isSoftCapped } = entitlement

  // There are other edge cases here which we do not handle since we'd have to be onboarded to use the JIL api. The JIL api would allow us to fetch org data. See https://developers.corp.adobe.com/abp/organizations/docs/apis/organizations/organization-contracts.md
  const cannotBuyCredits =
    (isEnterpriseUser && fireflyPlan === 'FF_30') ||
    (isPaidPremium && isSoftCapped)

  return !cannotBuyCredits
}

function* handleSetShowPaywall({
  payload
}: PayloadAction<PayloadType<typeof setShowPaywall>>) {
  // If the paywall is shown, we should hide any firefly toasts that are currently displayed so that when the paywall is closed, any toasts that may pop up at that point don't overlay the previous toasts that were already there
  if (payload !== 'none') {
    yield put(setShowCreditWarningToast(false))
    yield put(setShowPaywallToast(false))
  }
}

export default function* fireflySaga() {
  yield all([
    handleGenerateImages(),
    takeEvery(setShowFireflyPopover.type, changeFramePropertiesOnPopoverChange),
    takeEvery(setShowFireflyPopover.type, showOnboardingDialog),
    takeEvery(setPropertyState.type, handleSetPropertyState),
    takeEvery(resetState.type, handleResetState),
    takeEvery(stopImageGeneration.type, handleStopImageGeneration),
    takeEvery(generateSimilarImages.type, handleGenerateSimilarImages),
    takeEvery(selectGeneratedImage.type, handleSelectGeneratedImage),
    takeEvery(
      setShowLimitedFreeTimeToast.type,
      closeLimitedFreeTimeToastOnShow
    ),
    takeEvery(addDownloadURL.type, handleAddDownloadURL),
    takeEvery(setGeneratedImages.type, clearDownloadURLs),
    takeEvery(setOrderStatus.type, handleSetOrderStatus),
    takeEvery(
      setAccessProfileWorkflowResult.type,
      handleSetAccessProfileWorkflowResult
    ),
    takeEvery(loadFireflyCreditPlan.type, handleLoadFireflyCreditPlan),
    takeEvery(setFireflyCreditPlan.type, handleSetFireflyCreditPlan),
    takeEvery(setShowPaywall.type, handleSetShowPaywall)
  ])
}
